/* eslint-disable max-lines */
//
//  SDKDataCoreWriteRequests.ts
//  Supernova SDK
//
//  Created by Jiri Trecak.
//  Copyright © 2020 Supernova. All rights reserved.
//
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Imports
import { DTODesignSystem, } from "@supernova-studio/client";
import { StorybookUpdate } from "../../../model/storybook/SDKStorybookUpdate";
import { v4 as uuidv4 } from "uuid";
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Request engine: Write
export class DataCoreWriteRequests {
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Constructor
    constructor(bridge) {
        this.bridge = bridge;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - User
    /** Remote: Deletes the user by id. Can only really delete its own user */
    async deleteRemoteUser(userId) {
        const endpoint = this.bridge.getUserEndpoint(userId, null);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.bridge.delete(endpoint);
    }
    /** Remote: Checks whether user can be deleted or not. Can only really check its own user */
    // TODO:fix-sdk-eslint
    // eslint-disable-next-line @typescript-eslint/require-await
    async canDeleteRemoteUser(userId) {
        const endpoint = this.bridge.getUserEndpoint(userId, "check-deletable");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const result = this.bridge.post(endpoint, {}); // This will throw if not deletable
        return true;
    }
    /** Remote: Updates the user profile of specified user. This call can only be used on the user who is authenticated with the token */
    async updateRemoteUserProfile(profile, userId) {
        const endpoint = this.bridge.getUserEndpoint(userId, "profile");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.bridge.put(endpoint, profile);
    }
    /** Remote: Updates the avatar of the current user */
    async updateRemoteUserAvatar(userId, avatar) {
        const endpoint = this.bridge.getUserEndpoint(userId, "avatar");
        const formData = new FormData();
        formData.append("avatar", avatar);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.bridge.upload(endpoint, formData);
    }
    /** Remote: Updates the avatar of the current user */
    async deleteRemoteUserAvatar(userId) {
        const endpoint = this.bridge.getUserEndpoint(userId, "avatar");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.bridge.delete(endpoint);
    }
    /** Remote: Logs out user. Can only really logout its own user */
    async logoutRemoteCurrentUser() {
        const endpoint = this.bridge.getBaseEndpoint("users/me/logout");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.bridge.post(endpoint, null);
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Workspace
    /** Remote: Creates a new workspace */
    async createRemoteWorkspace(name) {
        const endpoint = this.bridge.getBaseEndpoint("workspaces");
        const payload = await this.bridge.post(endpoint, {
            name,
            product: "free",
            priceId: "yearly",
        });
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.workspace;
    }
    /** Remote: Creates a new version */
    async createRemoteVersion(designSystemId, model) {
        var _a, _b, _c;
        const endpoint = this.bridge.getDesignSystemEndpoint(designSystemId, "head/commit");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const response = await this.bridge.post(endpoint, {
            meta: {
                name: (_a = model.name) !== null && _a !== void 0 ? _a : "",
                description: (_b = model.description) !== null && _b !== void 0 ? _b : "",
            },
            version: model.version,
            changeLog: (_c = model.changeLog) !== null && _c !== void 0 ? _c : "",
        });
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return response.result;
    }
    /** Remote: Updates a version */
    async updateRemoteVersion(designSystemId, versionId, model) {
        var _a, _b, _c;
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, null);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.bridge.put(endpoint, {
            meta: {
                name: (_a = model.name) !== null && _a !== void 0 ? _a : "",
                description: (_b = model.description) !== null && _b !== void 0 ? _b : "",
            },
            version: model.version,
            changeLog: (_c = model.changeLog) !== null && _c !== void 0 ? _c : "",
        });
    }
    /** Remote: Deletes a version */
    async deleteRemoteVersion(designSystemId, versionId) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, null);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.bridge.delete(endpoint);
    }
    /** Remote: Creates a new brand */
    async createRemoteBrand(designSystemId, versionId, model) {
        var _a, _b;
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, "brands");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const response = await this.bridge.post(endpoint, {
            meta: {
                name: (_a = model.name) !== null && _a !== void 0 ? _a : "",
                description: (_b = model.description) !== null && _b !== void 0 ? _b : "",
            },
            persistentId: uuidv4(),
        });
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return response.result.brand;
    }
    /** Remote: Updates a brand */
    async updateRemoteBrand(designSystemId, versionId, brandId, brand) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, `brands/${brandId}`);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.bridge.put(endpoint, brand.toRemote());
    }
    /** Remote: Deletes a brand */
    async deleteRemoteBrand(designSystemId, versionId, brandId) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, `brands/${brandId}`);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.bridge.delete(endpoint);
    }
    /** Remote: Updates custom domain */
    async updateRemoteCustomDomain(designSystemId, customerDomain) {
        const endpoint = this.bridge.getDesignSystemEndpoint(designSystemId, "custom-domain");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.put(endpoint, { customerDomain });
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access
        return payload.result.customDomain;
    }
    /** Remote: Deletes custom domain */
    async deleteRemoteCustomDomain(designSystemId) {
        const endpoint = this.bridge.getDesignSystemEndpoint(designSystemId, "custom-domain");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.bridge.delete(endpoint);
    }
    async startSslGenerationForCustomDomain(designSystemId) {
        const endpoint = this.bridge.getDesignSystemEndpoint(designSystemId, "custom-domain/ssl");
        return this.bridge.post(endpoint, {});
    }
    /** Remote: Updates a workspace profile */
    async updateRemoteWorkspaceProfile(workspaceId, profile) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, "profile");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.bridge.put(endpoint, profile);
    }
    async testIpWhitelistSettings(workspaceId, ipWhitelistSettings) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, "ip-whitelist-test");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const response = await this.bridge.post(endpoint, ipWhitelistSettings);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access
        return response.result;
    }
    /** Remote: Updates a workspace avatar */
    async updateRemoteWorkspaceAvatar(workspaceId, avatar) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, "avatar");
        const formData = new FormData();
        formData.append("avatar", avatar);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.bridge.upload(endpoint, formData);
    }
    /** Remote: Updates a workspace onboarding */
    async updateRemoteWorkspaceOnboarding(workspaceId, onboarding) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, "onboarding");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.bridge.put(endpoint, onboarding);
    }
    /** Remote: Updates a workspace SSO settings */
    async updateRemoteWorkspaceSSOSettings(workspaceId, ssoSettings) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, "sso");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.bridge.put(endpoint, ssoSettings);
    }
    /** Remote: Updates a workspace IP whitelist settings */
    async updateRemoteWorkspaceIPWhitelistSettings(workspaceId, ipWhitelistSettings) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, "ip-whitelist");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.bridge.put(endpoint, ipWhitelistSettings);
    }
    /** Remote: Updates a workspace NPM registry settings */
    async updateRemoteWorkspaceNPMRegistrySettings(workspaceId, npmRegistrySettings) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, "npm-registry");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.bridge.put(endpoint, npmRegistrySettings);
    }
    /** Remote: Updates a notification settings */
    async updateUserWorkspaceNotificationSettings(userId, workspaceId, notificationSettings) {
        const endpoint = this.bridge.getUserWorkspaceEndpoint(userId, workspaceId, "notifications");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.bridge.post(endpoint, { notificationSettings });
    }
    /** Remote: Updates a workspace ownership */
    async updateRemoteWorkspaceOwnership(workspaceId, newOwnerUserId) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, "ownership");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.bridge.put(endpoint, {
            newOwnerId: newOwnerUserId,
        });
    }
    /** Remote: Updates a member role */
    async updateRemoteWorkspaceMemberRole(workspaceId, userId, newRole) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, "members/role");
        return this.bridge.put(endpoint, {
            members: [
                {
                    userId,
                    role: newRole,
                },
            ],
        });
    }
    /** Remote: Updates an invitation role */
    async updateRemoteWorkspaceInvitationRole(workspaceId, invitationId, newRole) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, `invitations/${invitationId}/role`);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.bridge.put(endpoint, {
            role: newRole,
        });
    }
    /** Remote: Add new invitations */
    async addRemoteWorkspaceInvitations(workspaceId, invitations, designSystemId) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, `members`);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.bridge.post(endpoint, {
            invites: invitations,
            designSystemId,
        });
    }
    /** Remote: Deletes a workspace */
    async deleteRemoteWorkspace(workspaceId) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, null);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.bridge.delete(endpoint);
    }
    /** Remote: Deletes a workspace avatar */
    async deleteRemoteWorkspaceAvatar(workspaceId) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, "avatar");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.bridge.delete(endpoint);
    }
    /** Remote: Deletes a workspace NPM settings */
    async deleteRemoteWorkspaceNPMRegistrySettings(workspaceId) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, "npm-registry");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.bridge.delete(endpoint);
    }
    /** Remote: Deletes a workspace member */
    async deleteRemoteWorkspaceMember(workspaceId, userId) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, `members/${userId}`);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.bridge.delete(endpoint);
    }
    /** Remote: Deletes an invitation */
    async deleteRemoteWorkspaceInvitation(workspaceId, invitationId) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, `invitations/${invitationId}`);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.bridge.delete(endpoint);
    }
    /** Remote: create Checkout session */
    async createRemoteCheckoutSession(workspaceId, priceId, successUrl, cancelUrl, quantity) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, `subscription/checkout-session`);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.post(endpoint, {
            priceId,
            successUrl,
            cancelUrl,
            quantity,
        });
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.url;
    }
    /** Remote: create Customer portal session */
    async createRemotePortalSession(workspaceId, returnUrl, update, cancel) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, `subscription/portal-session`);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.post(endpoint, Object.assign(Object.assign({ returnUrl }, (update && { update })), { cancel }));
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.url;
    }
    /** Remote: Resend the invitation */
    async resendRemoteWorkspaceInvitation(workspaceId, invitationId, designSystemId) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, `invitations/${invitationId}/resend`);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.bridge.post(endpoint, {
            invitationId,
            designSystemId,
        });
    }
    /** Remote: Restore the cancelled subscription */
    async restoreRemoteWorkspaceSubscription(workspaceId) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, `subscription/restore`);
        return this.bridge.post(endpoint, {});
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Design System
    /** Remote: Creates a new design system */
    async createRemoteDesignSystem(workspaceId, name, description, accessMode, usersToInvite, invitesToInvite, emailsToInvite) {
        const endpoint = this.bridge.getBaseEndpoint("design-systems");
        const payload = await this.bridge.post(endpoint, {
            meta: {
                name,
                description: description !== null && description !== void 0 ? description : "",
            },
            workspaceId,
            accessMode,
            invites: {
                usersToInvite,
                invitesToInvite,
                emailsToInvite,
            },
        });
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return DTODesignSystem.parse(payload.result.designSystem);
    }
    /** Remote: Updates a design system metadata */
    async updateRemoteDesignSystemMetadata(designSystemId, name, description) {
        const endpoint = this.bridge.getDesignSystemEndpoint(designSystemId, 
        // @ts-expect-error TS(2345): Argument of type 'undefined' is not assignable to ... Remove this comment to see the full error message
        undefined);
        await this.bridge.put(endpoint, {
            meta: {
                name,
                description: description !== null && description !== void 0 ? description : "",
            },
        });
    }
    /** Remote: Updates a design system multibrand capabilities */
    async updateRemoteDesignSystemIsMultibrand(designSystem, isMultibrand) {
        var _a;
        const endpoint = this.bridge.getDesignSystemEndpoint(designSystem.id, 
        // @ts-expect-error TS(2345): Argument of type 'undefined' is not assignable to ... Remove this comment to see the full error message
        undefined);
        await this.bridge.put(endpoint, {
            isMultibrand,
            meta: {
                name: designSystem.name,
                description: (_a = designSystem.description) !== null && _a !== void 0 ? _a : "",
            },
        });
    }
    /** Remote: Updates a design system approval feature */
    async updateRemoteDesignSystemApprovalsFeature(designSystemId, payload) {
        const endpoint = this.bridge.getDesignSystemEndpoint(designSystemId, null);
        await this.bridge.put(endpoint, payload);
    }
    /** Remote: Updates a design system switcher */
    async updateRemoteDesignSystemSwitcher(designSystem, designSystemSwitcher) {
        const endpoint = this.bridge.getDesignSystemEndpoint(designSystem.id, null);
        await this.bridge.put(endpoint, {
            meta: {
                name: designSystem.name,
                description: designSystem.description,
            },
            designSystemSwitcher,
        });
    }
    /** Remote: Updates design system doc exporter id */
    async updateRemoteDesignSystemDocExporterId(designSystem, docExporterId) {
        const endpoint = this.bridge.getDesignSystemEndpoint(designSystem.id, 
        // @ts-expect-error TS(2345): Argument of type 'undefined' is not assignable to ... Remove this comment to see the full error message
        undefined);
        await this.bridge.put(endpoint, {
            meta: {
                name: designSystem.name,
                description: designSystem.description,
            },
            docExporterId,
        });
    }
    /** Remote: Update documentation structure */
    async updateRemoteDocumentationStructure(designSystemId, versionId, model) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, "documentation");
        await this.bridge.put(endpoint, model);
    }
    /** Remote: Updates a design system documentation slug */
    async updateRemoteDesignSystemDocumentationSlug(designSystem, docUserSlug) {
        const endpoint = this.bridge.getDesignSystemEndpoint(designSystem.id, 
        // @ts-expect-error TS(2345): Argument of type 'undefined' is not assignable to ... Remove this comment to see the full error message
        undefined);
        await this.bridge.put(endpoint, {
            meta: {
                name: designSystem.name,
                description: designSystem.description,
            },
            docUserSlug,
        });
    }
    /** Remote: Updates a design system's access mode and, if switching to invite-only, also the member list */
    async updateRemoteDesignSystemAccessModeAndMemberList(designSystemId, accessMode, retainUserIds, retainInviteIds) {
        const endpoint = this.bridge.getDesignSystemEndpoint(designSystemId, "access-mode");
        await this.bridge.put(endpoint, {
            accessMode,
            retain: {
                userIds: retainUserIds,
                inviteIds: retainInviteIds,
            },
        });
    }
    /** Remote: Updates a design system's member list */
    async updateRemoteDesignSystemMemberList(designSystemId, usersToInvite, invitesToInvite, emailsToInvite, invitesToUpdate, deleteInvitationIds, usersToUpdate, removeUserIds) {
        const endpoint = this.bridge.getDesignSystemEndpoint(designSystemId, "members");
        await this.bridge.post(endpoint, {
            usersToInvite,
            invitesToInvite,
            emailsToInvite,
            invitesToUpdate,
            deleteInvitationIds,
            usersToUpdate,
            removeUserIds,
        });
    }
    /** Remote: Deletes a design system */
    async deleteDesignSystem(designSystemId) {
        const endpoint = this.bridge.getDesignSystemEndpoint(designSystemId, null);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.bridge.delete(endpoint);
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Documentation
    async elementAction(designSystemId, versionId, data) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, "elements-action");
        return this.bridge.post(endpoint, data);
    }
    async updateElementProperties(designSystemId, versionId, data) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, "elements-action");
        return this.bridge.post(endpoint, data);
    }
    async createRemoteDocumentationPage(designSystemId, versionId, data) {
        return this.elementAction(designSystemId, versionId, {
            type: "DocumentationPageCreate",
            input: data,
        });
    }
    async updateApprovalState(designSystemId, versionId, data) {
        return this.elementAction(designSystemId, versionId, {
            type: "DocumentationPageApprovalStateChange",
            input: data,
        });
    }
    /** Remote: create a group within documentation */
    async createRemoteDocumentationGroup(designSystemId, versionId, data) {
        return this.elementAction(designSystemId, versionId, {
            type: "DocumentationGroupCreate",
            input: data,
        });
    }
    /** Remote: create a group within documentation */
    async createRemoteDocumentationTab(designSystemId, versionId, data) {
        return this.elementAction(designSystemId, versionId, {
            type: "DocumentationTabCreate",
            input: data,
        });
    }
    /** Remote: update a documentation page */
    async updateRemoteDocumentationPage(designSystemId, versionId, data) {
        return this.elementAction(designSystemId, versionId, {
            type: "DocumentationPageUpdate",
            input: data,
        });
    }
    /** Remote: updates a documentation group */
    async updateRemoteDocumentationGroup(designSystemId, versionId, data) {
        return this.elementAction(designSystemId, versionId, {
            type: "DocumentationGroupUpdate",
            input: data,
        });
    }
    /** Remote: moves a documentation page */
    async moveRemoteDocumentationPage(designSystemId, versionId, data) {
        return this.elementAction(designSystemId, versionId, {
            type: "DocumentationPageMove",
            input: data,
        });
    }
    /** Remote: moves a documentation group */
    async moveRemoteDocumentationGroup(designSystemId, versionId, data) {
        return this.elementAction(designSystemId, versionId, {
            type: "DocumentationGroupMove",
            input: data,
        });
    }
    /** Remote: creates a duplicate of a documentation page */
    async duplicateRemoteDocumentationPage(designSystemId, versionId, data) {
        return this.elementAction(designSystemId, versionId, {
            type: "DocumentationPageDuplicate",
            input: data,
        });
    }
    /** Remote: creates a duplicate of a documentation group */
    async duplicateRemoteDocumentationGroup(designSystemId, versionId, data) {
        return this.elementAction(designSystemId, versionId, {
            type: "DocumentationGroupDuplicate",
            input: data,
        });
    }
    /** Remote: deletes a documentation page */
    async deleteRemoteDocumentationPage(designSystemId, versionId, data) {
        return this.elementAction(designSystemId, versionId, {
            type: "DocumentationPageDelete",
            input: data,
        });
    }
    /** Remote: deletes a documentation tab group */
    async deleteRemoteDocumentationTabGroup(designSystemId, versionId, data) {
        return this.elementAction(designSystemId, versionId, {
            type: "DocumentationTabGroupDelete",
            input: data,
        });
    }
    async restoreRemoteDocumentationGroup(designSystemId, versionId, data) {
        return this.elementAction(designSystemId, versionId, {
            type: "DocumentationGroupRestore",
            input: data,
        });
    }
    async restoreRemoteDocumentationPage(designSystemId, versionId, data) {
        return this.elementAction(designSystemId, versionId, {
            type: "DocumentationPageRestore",
            input: data,
        });
    }
    /** Remote: Deletes a documentation group */
    async deleteRemoteDocumentationGroup(designSystemId, versionId, data) {
        return this.elementAction(designSystemId, versionId, {
            type: "DocumentationGroupDelete",
            input: data,
        });
    }
    async publishDocumentation(workspaceId, designSystemId, versionId, environment) {
        const endpoint = this.bridge.getBaseEndpoint(`codegen/workspaces/${workspaceId}/jobs/documentation`);
        const payload = {
            designSystemId,
            designSystemVersionId: versionId,
            environment,
        };
        const response = await this.bridge.post(endpoint, payload);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return response.result.job;
    }
    async publishDocumentationDrafts(designSystemId, versionId, environment, changes) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, "documentation/publish");
        const payload = {
            environment,
            changes,
        };
        const response = await this.bridge.post(endpoint, payload);
        return response.result.job;
    }
    /** Remote: Render specific figma frames */
    async renderFigmaFrames(designSystemId, versionId, data) {
        const payload = await this.elementAction(designSystemId, versionId, {
            type: "FigmaNodeRender",
            input: data,
        });
        return payload.result.figmaNodes;
    }
    /** Remote: Render specific figma frames async */
    async renderFigmaFramesAsync(designSystemId, versionId, data) {
        const payload = await this.elementAction(designSystemId, versionId, {
            type: "FigmaNodeRenderAsync",
            nodes: data,
        });
        return payload.result.figmaNodes;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Tokens
    async createRemoteToken(designSystemId, versionId, token) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, "tokens");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.post(endpoint, token);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.token;
    }
    async updateRemoteToken(designSystemId, versionId, token) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, `tokens/${token.id}`);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.bridge.put(endpoint, token);
    }
    async createRemoteTokenGroup(designSystemId, versionId, group) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, "token-groups");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.post(endpoint, group);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.group;
    }
    async updateRemoteTokenGroup(designSystemId, versionId, group) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, 
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `token-groups/${group.id}`);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.bridge.put(endpoint, group);
    }
    async deleteRemoteToken(designSystemId, versionId, tokenId) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, `tokens/${tokenId}`);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.bridge.delete(endpoint);
    }
    async ungroupRemoteTokenGroup(designSystemId, versionId, tokenGroupId) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, `token-groups/${tokenGroupId}`);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.bridge.delete(endpoint);
    }
    async createRemoteTokenTheme(designSystemId, versionId, theme) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, "themes");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.post(endpoint, theme);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.theme;
    }
    async updateRemoteTokenTheme(designSystemId, versionId, group) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, 
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `themes/${group.id}`);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.bridge.put(endpoint, group);
    }
    async deleteRemoteTokenTheme(designSystemId, versionId, themeId) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, `themes/${themeId}`);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.bridge.delete(endpoint);
    }
    async createThemeOverrideForToken(designSystemId, versionId, themeId, override) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, `themes/${themeId}/overrides`);
        return this.bridge.post(endpoint, override);
    }
    async updateThemeOverrideForToken(designSystemId, versionId, tokenPersistentId, themeId, override) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, `themes/${themeId}/overrides/${tokenPersistentId}`);
        return this.bridge.put(endpoint, override);
    }
    async deleteThemeOverrideForToken(designSystemId, versionId, tokenPersistentId, themeId) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, `themes/${themeId}/overrides/${tokenPersistentId}`);
        return this.bridge.delete(endpoint);
    }
    async writeTokenData(designSystemId, versionId, tokens, groups, deleteTokens, deleteTokenGroups) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, "bff/import-legacy");
        const payload = {
            tokens,
            tokenGroups: groups,
            bulkDelete: {
                tokenIds: deleteTokens.map((t) => t.idInVersion),
                tokenGroupIds: deleteTokenGroups.map((t) => t.idInVersion),
            },
        };
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.bridge.post(endpoint, payload);
    }
    async writeTokenThemeData(designSystemId, versionId, theme) {
        const payload = theme;
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, 
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        `themes/${theme.id}`);
        await this.bridge.put(endpoint, payload);
    }
    async updateTokenPropertyValue(designSystemId, versionId, newValue, tokenId, propertyId) {
        const payload = {
            value: newValue,
            definitionId: propertyId,
            targetElementId: tokenId,
        };
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, `element-properties/values`);
        await this.bridge.post(endpoint, payload);
    }
    async deleteTokenPropertyValue(designSystemId, versionId, valueId) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, `element-properties/values/${valueId}`);
        await this.bridge.delete(endpoint);
    }
    async createTokenProperty(designSystemId, versionId, model) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, "element-properties/definitions");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.post(endpoint, model);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.definition;
    }
    async updateTokenProperty(designSystemId, versionId, propertyIdInVersion, model) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, `element-properties/definitions/${propertyIdInVersion}`);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.bridge.put(endpoint, model);
    }
    async deleteTokenProperty(designSystemId, versionId, propertyId) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, `element-properties/definitions/${propertyId}`);
        await this.bridge.delete(endpoint);
    }
    async updateReorderTokenColumn(designSystemId, versionId, viewId, columnId, newIndex) {
        const payload = {
            newIndex,
        };
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, `element-data-views/${viewId}/columns/${columnId}/reorder`);
        await this.bridge.put(endpoint, payload);
    }
    async updateResizeTokenColumn(designSystemId, versionId, viewId, columnType, columnId, columnThemeId, columnBasePropertyType, columnPropertyDefinitionId, newWidth) {
        const payload = {
            type: columnType,
            width: newWidth,
            themeId: columnThemeId,
            basePropertyType: columnBasePropertyType,
            propertyDefinitionId: columnPropertyDefinitionId,
        };
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, `element-data-views/${viewId}/columns/${columnId}`);
        await this.bridge.put(endpoint, payload);
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Resources
    /** Remote: Upload a new asset resource */
    async uploadAssetResource(designSystemId, versionId, asset) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, "assets/image?scope=Documentation");
        const formData = new FormData();
        formData.append("asset", asset);
        const result = await this.bridge.upload(endpoint, formData, "POST");
        return result.result.asset;
    }
    /** Remote: Upload a new font resource */
    async uploadFontResource(designSystemId, versionId, font) {
        var _a, _b;
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, "assets/font?scope=DesignSystem");
        const formData = new FormData();
        formData.append("asset", font);
        const result = await this.bridge.upload(endpoint, formData, "POST");
        if ((_b = (_a = result === null || result === void 0 ? void 0 : result.result) === null || _a === void 0 ? void 0 : _a.assets) === null || _b === void 0 ? void 0 : _b[0]) {
            return result.result.assets[0];
        }
        return null;
    }
    /** Remote: Upload a new font resources */
    async uploadFontResources(designSystemId, versionId, fonts) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, "assets/font?scope=DesignSystem");
        const formData = new FormData();
        // eslint-disable-next-line no-plusplus
        for (let i = 0; i < fonts.length; i++) {
            const font = fonts.item(i);
            if (!font) {
                continue;
            }
            formData.append("asset", font);
        }
        const result = await this.bridge.upload(endpoint, formData, "POST");
        return result.result.assets;
    }
    /** Remote: Delete a resource */
    async deleteResource(designSystemId, versionId, resourceId) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, `assets/${resourceId}`);
        return this.bridge.delete(endpoint);
    }
    /** Remote: Delete resources */
    async deleteResources(designSystemId, versionId, resourceIds) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, `assets/batch-delete`);
        return this.bridge.post(endpoint, {
            ids: resourceIds,
        });
    }
    async initializeFileUpload(designSystemId, versionId, fileUploadItems) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, "files/upload");
        const result = await this.bridge.post(endpoint, {
            files: fileUploadItems,
        });
        return result.result;
    }
    async finalizeFileUpload(designSystemId, versionId, fileIds) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, "files/finalize");
        return this.bridge.post(endpoint, {
            fileIds,
        });
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Components
    async createRemoteComponent(designSystemId, versionId, component) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, "design-system-components");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.post(endpoint, component);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.designSystemComponent;
    }
    async updateRemoteComponent(designSystemId, versionId, component) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, `design-system-components/${component.id}`);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.bridge.put(endpoint, component);
    }
    async createRemoteComponentGroup(designSystemId, versionId, group) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, "design-system-component-groups");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.post(endpoint, group);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.group;
    }
    async updateRemoteComponentGroup(designSystemId, versionId, group) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, `design-system-component-groups/${group.id}`);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.bridge.put(endpoint, group);
    }
    async deleteRemoteComponent(designSystemId, versionId, componentId) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, `design-system-components/${componentId}`);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.bridge.delete(endpoint);
    }
    async ungroupRemoteComponentGroup(designSystemId, versionId, componentGroupId) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, `design-system-component-groups/${componentGroupId}`);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.bridge.delete(endpoint);
    }
    async writeComponentData(designSystemId, versionId, components, groups, deleteComponents, deleteComponentGroups) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, "bff/import");
        const payload = {
            components,
            componentGroups: groups,
            bulkDelete: {
                componentIds: deleteComponents.map((t) => t.idInVersion),
                componentGroupIds: deleteComponentGroups.map((t) => t.idInVersion),
            },
        };
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.bridge.post(endpoint, payload);
    }
    async updateComponentPropertyValue(designSystemId, versionId, newValue, componentId, propertyId) {
        const payload = {
            value: newValue,
            definitionId: propertyId,
            targetElementId: componentId,
        };
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, `element-properties/values`);
        await this.bridge.post(endpoint, payload);
    }
    async deleteComponentPropertyValue(designSystemId, versionId, valueId) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, `element-properties/values/${valueId}`);
        await this.bridge.delete(endpoint);
    }
    async createComponentProperty(designSystemId, versionId, model) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, "element-properties/definitions");
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.post(endpoint, model);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.definition;
    }
    async updateComponentProperty(designSystemId, versionId, propertyIdInVersion, model) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, `element-properties/definitions/${propertyIdInVersion}`);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.bridge.put(endpoint, model);
    }
    async deleteComponentProperty(designSystemId, versionId, propertyId) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, `element-properties/definitions/${propertyId}`);
        await this.bridge.delete(endpoint);
    }
    async updateReorderComponentColumn(designSystemId, versionId, viewId, columnId, newIndex) {
        const payload = {
            newIndex,
        };
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, `element-data-views/${viewId}/columns/${columnId}/reorder`);
        await this.bridge.put(endpoint, payload);
    }
    async updateResizeComponentColumn(designSystemId, versionId, viewId, columnType, columnId, columnThemeId, columnBasePropertyType, columnPropertyDefinitionId, newWidth) {
        const payload = {
            type: columnType,
            width: newWidth,
            themeId: columnThemeId,
            basePropertyType: columnBasePropertyType,
            propertyDefinitionId: columnPropertyDefinitionId,
        };
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, `element-data-views/${viewId}/columns/${columnId}`);
        await this.bridge.put(endpoint, payload);
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Integrations
    /** Remote: Deletes a OAuth integration */
    async deleteOAuthIntegration(userId, service) {
        const endpoint = this.bridge.getBaseEndpoint(`users/${userId}/integration/${service}`);
        await this.bridge.delete(endpoint);
    }
    /** Remote: Create Authentication Token */
    async createAuthenticationToken(name, scope) {
        const endpoint = this.bridge.getBaseEndpoint(`auth/tokens`);
        return (await this.bridge.post(endpoint, { name, scope })).result
            .token;
    }
    /** Remote: Delete Authentication Token */
    async deleteAuthenticationToken(id) {
        const endpoint = this.bridge.getBaseEndpoint(`auth/tokens/${id}`);
        return (await this.bridge.delete(endpoint))
            .result;
    }
    /** Remote: Create Personal Access token connection */
    async createPersonalAccessTokenConnection(service, connection) {
        const endpoint = this.bridge.getBaseEndpoint(`codegen/${service}/connections`);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return (await this.bridge.post(endpoint, connection)).result
            .connection;
    }
    /** Remote: Create Personal Access token credential for a workspace integration */
    async createRemoteWorkspaceIntegrationAccessToken(workspaceId, data) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, "integrations");
        return (await this.bridge.post(endpoint, data))
            .result;
    }
    /** Remote: Delete credential integration connection from workspace */
    async deleteRemoteWorkspaceIntegrationCredential(workspaceId, integrationId, credentialId) {
        const endpoint = this.bridge.getWorkspaceIntegrationCredentialEndpoint(workspaceId, integrationId, credentialId);
        return (await this.bridge.delete(endpoint))
            .result;
    }
    /** Remote: Delete a workspace integration */
    async deleteRemoteWorkspaceIntegration(workspaceId, integrationId) {
        const endpoint = this.bridge.getWorkspaceIntegrationEndpoint(workspaceId, integrationId);
        return (await this.bridge.delete(endpoint))
            .result;
    }
    /** Remote: Create pipeline */
    async createRemotePipeline(workspaceId, payload) {
        const endpoint = this.bridge.getBaseEndpoint(`codegen/workspaces/${workspaceId}/pipelines`);
        return (await this.bridge.post(endpoint, payload)).result
            .pipeline;
    }
    /** Remote: Update pipeline */
    async updateRemotePipeline(designSystemId, pipelineId, payload) {
        const endpoint = this.bridge.getBaseEndpoint(`design-systems/${designSystemId}/pipelines/${pipelineId}`);
        return (await this.bridge.put(endpoint, payload)).result
            .pipeline;
    }
    /** Remote: Run pipeline */
    async runRemotePipeline(workspaceId, designSystemVersionId, pipelineId) {
        const endpoint = this.bridge.getBaseEndpoint(`codegen/workspaces/${workspaceId}/pipelines/${pipelineId}/trigger`);
        const response = await this.bridge.post(endpoint, {
            designSystemVersionId,
        });
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return response.result.job;
    }
    /** Remote: Delete pipeline */
    async deleteRemotePipeline(workspaceId, pipelineId) {
        const endpoint = this.bridge.getBaseEndpoint(`codegen/workspaces/${workspaceId}/schedules/${pipelineId}`);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.bridge.delete(endpoint);
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Exporters
    /** Remote: Create exporter */
    async createRemoteExporter(workspaceId, payload) {
        const endpoint = this.bridge.getBaseEndpoint(`codegen/workspaces/${workspaceId}/exporters`);
        return (await this.bridge.post(endpoint, payload)).result.exporter;
    }
    /** Remote: Updates an exporter */
    async updateRemoteExporter(workspaceId, exporterId, payload) {
        const endpoint = this.bridge.getBaseEndpoint(`codegen/workspaces/${workspaceId}/exporters/${exporterId}`);
        return this.bridge.put(endpoint, payload);
    }
    /** Remote: Deletes an exporter */
    async deleteRemoteExporter(workspaceId, exporterId) {
        const endpoint = this.bridge.getBaseEndpoint(`codegen/workspaces/${workspaceId}/exporters/${exporterId}`);
        return this.bridge.delete(endpoint);
    }
    /** Remote: Updates an exporter configuration property value */
    async updateRemoteExporterConfigurationPropertyValue(designSystemId, exporterId, propertyKey, propertyValue) {
        const endpoint = this.bridge.getDesignSystemEndpoint(designSystemId, `exporter-properties/${exporterId}/${propertyKey}`);
        return this.bridge.put(endpoint, { value: propertyValue });
    }
    /** Remote: Deletes an exporter configuration property value */
    async deleteRemoteExporterConfigurationPropertyValue(designSystemId, exporterId, propertyKey) {
        const endpoint = this.bridge.getDesignSystemEndpoint(designSystemId, `exporter-properties/${exporterId}/${propertyKey}`);
        return this.bridge.delete(endpoint);
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Builds
    /** Remote: Create a new exporter build */
    async createExporterBuild(workspaceId, designSystemId, versionId, exporterId, destinations, brandId, themePersistentIds, exporterConfigurationProperties, previewMode) {
        const endpoint = this.bridge.getBaseEndpoint(`codegen/workspaces/${workspaceId}/jobs`);
        const payload = {
            designSystemId,
            designSystemVersionId: versionId,
            exporterId,
            destinations,
            brandId,
            themePersistentIds,
            exporterPropertyValues: exporterConfigurationProperties,
            previewMode,
        };
        const response = await this.bridge.post(endpoint, payload);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return response.result.job;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Jobs
    /** Remote: Schedules import job */
    async scheduleImportJobForFigmaSources(designSystemId, versionId, sourceIds, dryRun) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, "cloud-import");
        const data = {
            sourceIds,
            dryRun,
        };
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.post(endpoint, data);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.job;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Sources
    /** Remote: Deletes a remote data source */
    async deleteRemoteSource(designSystemId, sourceId) {
        const endpoint = this.bridge.getDesignSystemEndpoint(designSystemId, `sources/${sourceId}`);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return this.bridge.delete(endpoint);
    }
    async linkSource(designSystemId, data) {
        const endpoint = this.bridge.getDesignSystemEndpoint(designSystemId, `sources`);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.post(endpoint, data);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.source;
    }
    async updateSource(designSystemId, sourceId, data) {
        const endpoint = this.bridge.getDesignSystemEndpoint(designSystemId, `sources/${sourceId}`);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const payload = await this.bridge.put(endpoint, data);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        return payload.result.source;
    }
    async updateStorybookSource(designSystemId, versionId, data) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, "storybook-import");
        const payload = await this.bridge.post(endpoint, data);
        return new StorybookUpdate(payload.result);
    }
    async replaceStorybookStory(designSystemId, versionId, storyId, data) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, `storybook/${storyId}`);
        const payload = await this.bridge.put(endpoint, data);
        return payload.result;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Import
    async writeTokenStudioJSONData(designSystemId, versionId, data) {
        const endpoint = this.bridge.getVersionEndpoint(designSystemId, versionId, "bff/token-studio");
        const payload = this.bridge.post(endpoint, data);
        return payload;
    }
    async importTokens(designSystemId, brandId, sourceId, sourceName, sourceType, importType, payload, isTokenTypeSplitEnabled) {
        const endpoint = this.bridge.getDesignSystemEndpoint(designSystemId, "bff/import");
        const data = Object.assign(Object.assign({ brandPersistentId: brandId, sourceName, remoteId: sourceId, remoteSourceType: sourceType, type: importType }, (isTokenTypeSplitEnabled !== undefined && { isTokenTypeSplitEnabled })), { 
            // TODO:fix-sdk-eslint
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            payload });
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const response = await this.bridge.post(endpoint, data);
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access
        return response.result.job;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Storage
    async setKeyValueStorageData(designSystemId, kvData) {
        const endpoint = this.bridge.getBaseEndpoint(`design-systems/${designSystemId}/metadata`);
        await this.bridge.put(endpoint, kvData);
    }
    async createPageRedirect(designSystemId, body) {
        const endpoint = this.bridge.getDesignSystemEndpoint(designSystemId, "documentation/redirects");
        await this.bridge.post(endpoint, body);
    }
    async updatePageRedirect(designSystemId, redirectId, body) {
        const endpoint = this.bridge.getDesignSystemEndpoint(designSystemId, `documentation/redirects/${redirectId}`);
        await this.bridge.put(endpoint, body);
    }
    async deletePageRedirect(designSystemId, redirectId) {
        const endpoint = this.bridge.getDesignSystemEndpoint(designSystemId, `documentation/redirects/${redirectId}`);
        await this.bridge.delete(endpoint);
    }
    async createRemoteUntypedData(workspaceId, data) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, "untyped-data");
        const payload = await this.bridge.post(endpoint, data);
        return payload.result;
    }
    async deleteRemoteAllUntypedData(workspaceId) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, "untyped-data");
        await this.bridge.delete(endpoint);
    }
    async updateRemoteUntypedData(workspaceId, id, data) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, `untyped-data/${id}`);
        const payload = await this.bridge.put(endpoint, data);
        return payload.result;
    }
    async deleteRemoteUntypedData(workspaceId, id) {
        const endpoint = this.bridge.getWorkspaceEndpoint(workspaceId, `untyped-data/${id}`);
        await this.bridge.delete(endpoint);
    }
}
